package br.com.vvrefeicoes.portal.data.repository

import br.com.vvrefeicoes.portal.domain.model.FoodPassBilling
import br.com.vvrefeicoes.portal.domain.model.FoodPassClient
import br.com.vvrefeicoes.portal.domain.model.FoodPassConsumption
import br.com.vvrefeicoes.portal.domain.model.FoodPassStatementEntry
import br.com.vvrefeicoes.portal.domain.repository.AuthRepository
import br.com.vvrefeicoes.portal.domain.repository.FoodPassRepository
import io.ktor.client.HttpClient
import io.ktor.client.call.body
import io.ktor.client.request.get
import io.ktor.client.request.header
import io.ktor.client.request.post
import io.ktor.client.request.setBody
import io.ktor.client.statement.HttpResponse
import io.ktor.http.ContentType
import io.ktor.http.HttpHeaders
import io.ktor.http.contentType
import kotlinx.serialization.Serializable
import kotlin.math.roundToLong

class RemoteFoodPassRepository(
    private val httpClient: HttpClient,
    private val baseUrl: String,
    private val authRepository: AuthRepository,
) : FoodPassRepository {
    override suspend fun createClient(
        name: String,
        document: String,
        email: String,
    ): FoodPassClient = post<ClientDto>("/food-pass/clients", ClientRequestDto(name, document, email)).toDomain()

    override suspend fun loadClients(): List<FoodPassClient> =
        get<FoodPassPageDto<ClientDto>>("/food-pass/clients?size=100")
            .content
            .map(ClientDto::toDomain)

    override suspend fun loadStatement(clientId: String): List<FoodPassStatementEntry> =
        get<List<StatementDto>>("/food-pass/clients/$clientId/statement")
            .map(StatementDto::toDomain)

    override suspend fun loadConsumptions(
        companyId: String,
        from: String?,
        to: String?,
    ): List<FoodPassConsumption> =
        get<List<ConsumptionDto>>(companyQuery("/food-pass/consumptions", companyId, from, to))
            .map(ConsumptionDto::toDomain)

    override suspend fun loadBilling(
        companyId: String,
        from: String?,
        to: String?,
    ): FoodPassBilling =
        get<BillingDto>(companyQuery("/food-pass/billing", companyId, from, to))
            .toDomain()

    private suspend inline fun <reified T> post(
        path: String,
        body: Any,
    ): T =
        request {
            httpClient.post(baseUrl + path) {
                contentType(ContentType.Application.Json)
                setBody(body)
                authorize()
            }
        }.body()

    private suspend inline fun <reified T> get(path: String): T = request { httpClient.get(baseUrl + path) { authorize() } }.body()

    private suspend inline fun request(crossinline call: suspend () -> HttpResponse): HttpResponse {
        val response = call()
        if (response.status.value !in 200..299) {
            error("Food Pass: HTTP ${response.status.value}")
        }
        return response
    }

    private fun io.ktor.client.request.HttpRequestBuilder.authorize() {
        authRepository.session.value
            ?.token
            ?.let { header(HttpHeaders.Authorization, "Bearer $it") }
    }
}

@Serializable
private data class FoodPassPageDto<T>(
    val content: List<T> = emptyList(),
)

@Serializable
private data class ClientRequestDto(
    val nome: String,
    val documento: String? = null,
    val email: String? = null,
)

@Serializable
private data class ClientDto(
    val id: String,
    val nome: String = "",
    val documento: String? = null,
    val email: String? = null,
    val status: Boolean = true,
    val balanceCents: Long = 0,
)

@Serializable
private data class ConsumptionDto(
    val id: String,
    val companyId: String,
    val clientId: String,
    val companyServiceId: String,
    val nfcId: String,
    val amount: Double = 0.0,
    val balance: Double = 0.0,
    val clientName: String? = null,
    val serviceName: String? = null,
    val consumedAt: String,
)

@Serializable
private data class StatementDto(
    val type: String,
    val amountCents: Long,
    val direction: String,
    val description: String,
    val referenceId: String? = null,
    val occurredAt: String,
)

@Serializable
private data class BillingDto(
    val companyId: String,
    val from: String,
    val to: String,
    val consumptionCount: Long,
    val totalCents: Long,
)

private fun companyQuery(
    path: String,
    companyId: String,
    from: String?,
    to: String?,
): String =
    buildString {
        append(path)
        append("?companyId=")
        append(companyId)
        from?.let {
            append("&from=")
            append(it)
        }
        to?.let {
            append("&to=")
            append(it)
        }
    }

private fun ClientDto.toDomain() =
    FoodPassClient(
        id,
        nome,
        documento.orEmpty(),
        email.orEmpty(),
        status,
        balanceCents,
    )

private fun ConsumptionDto.toDomain() =
    FoodPassConsumption(
        id,
        companyId,
        clientId,
        companyServiceId,
        nfcId,
        (amount * 100).roundToLong(),
        consumedAt,
        clientName,
        serviceName,
        (balance * 100).roundToLong(),
    )

private fun StatementDto.toDomain() =
    FoodPassStatementEntry(
        type,
        amountCents,
        direction,
        description,
        referenceId,
        occurredAt,
    )

private fun BillingDto.toDomain() =
    FoodPassBilling(
        companyId,
        from,
        to,
        consumptionCount,
        totalCents,
    )
